import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Users, UserPen } from "lucide-react";
import { generateAvatarPlaceholder } from "@/lib/utils";

interface FamilyMember {
  id: string;
  name: string;
  relationship: string;
  accessLevel: string;
  isActive: boolean;
  profileImageUrl?: string;
  email?: string;
}

interface InTheLoopListProps {
  familyMembers: FamilyMember[];
  athleteId?: string;
  onAddMember?: () => void;
}

const FamilyCircleList = ({ 
  familyMembers = [], 
  athleteId,
  onAddMember 
}: InTheLoopListProps) => {
  const activeMembers = familyMembers.filter(member => member.isActive);
  
  const getAccessLevelIcon = (accessLevel: string) => {
    switch (accessLevel) {
      case "admin":
        return <span className="material-icons text-primary text-sm">shield</span>;
      case "editor":
        return <span className="material-icons text-secondary text-sm">edit</span>;
      case "viewer":
        return <span className="material-icons text-accent text-sm">visibility</span>;
      case "limited":
        return <span className="material-icons text-slate-300 text-sm">lock</span>;
      default:
        return <span className="material-icons text-slate-300 text-sm">account_circle</span>;
    }
  };

  const getRelationshipLabel = (relationship: string) => {
    return relationship.charAt(0).toUpperCase() + relationship.slice(1);
  };

  return (
    <Card className="shadow-md overflow-hidden">
      <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Users className="text-primary mr-2 h-5 w-5" />
            <CardTitle className="font-montserrat font-bold text-lg text-white">
              In the Loop
            </CardTitle>
          </div>
          {onAddMember && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-primary hover:text-primary/80" 
              onClick={onAddMember}
            >
              <UserPen className="h-5 w-5" />
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-4">
        {familyMembers.length === 0 ? (
          <div className="text-center py-4">
            <Users className="h-10 w-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-200">No one in The Loop yet</p>
            {onAddMember && (
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3 border-[#A67C52] text-white bg-[#111827] hover:bg-[#18233a]"
                onClick={onAddMember}
              >
                <UserPen className="h-4 w-4 mr-1" /> Add to The Loop
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {activeMembers.map((member) => (
              <div key={member.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Avatar>
                    <AvatarImage 
                      src={member.profileImageUrl} 
                      alt={member.name} 
                    />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {generateAvatarPlaceholder(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-white">{member.name}</p>
                    <p className="text-slate-300 text-xs flex items-center">
                      {getRelationshipLabel(member.relationship)} • 
                      <span className="flex items-center ml-1">
                        {getAccessLevelIcon(member.accessLevel)}
                        <span className="ml-0.5 capitalize">{member.accessLevel}</span>
                      </span>
                    </p>
                  </div>
                </div>
                <div>
                  <Badge 
                    variant="outline" 
                    className={`${member.isActive ? 'bg-green-50 text-green-600' : 'bg-neutral-100 text-slate-300'}`}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full ${member.isActive ? 'bg-emerald-400' : 'bg-slate-400'} mr-1`}></span>
                    {member.isActive ? 'Active' : 'Pending'}
                  </Badge>
                </div>
              </div>
            ))}
            
            {/* Pending invitations summary */}
            {familyMembers.length > activeMembers.length && (
              <div className="pt-3 mt-3 border-t border-[#A67C52]">
                <p className="text-sm text-slate-200">
                  {familyMembers.length - activeMembers.length} pending invitation{familyMembers.length - activeMembers.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
        )}
        
        {familyMembers.length > 0 && onAddMember && (
          <Button 
            variant="outline" 
            className="mt-4 w-full border-[#A67C52] text-white bg-[#111827] hover:bg-[#18233a]"
            onClick={onAddMember}
          >
            Manage access settings
            <span className="material-icons ml-1 text-sm">settings</span>
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default FamilyCircleList;
